import { useEffect } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { StarIcon } from 'lucide-react';

import { Badge } from '@nesso/ui/primitives/badge';
import { Button } from '@nesso/ui/primitives/button';

import {
    formatAddressLine,
    type PracticeLocation,
    practiceLocationsOptions,
} from '@/entities/practice';
import { RemoveLocationButton } from '@/features/practice/remove-location';
import { SaveLocation } from '@/features/practice/save-location';
import { SetDefaultLocationButton } from '@/features/practice/set-default-location';
import { SettingsRow, SettingsRowActionButton } from '@/shared/ui/settings/settings-row';
import { SettingsSection } from '@/widgets/settings/settings-section';

import type { PracticeDetailsEditing } from '../../model/use-practice-details-editing';

const LOCATION_KEY_PREFIX = 'location:';
const ADD_LOCATION_KEY = `${LOCATION_KEY_PREFIX}add`;
const locationKey = (id: string) => `${LOCATION_KEY_PREFIX}${id}`;

function DefaultMarker() {
    return (
        <Badge variant="info" className="gap-1">
            <StarIcon className="size-3 fill-current" />
            Default
        </Badge>
    );
}

type LocationRowProps = {
    location: PracticeLocation;
    editing: PracticeDetailsEditing;
};

function LocationRow({ location, editing }: LocationRowProps) {
    const key = locationKey(location.id);

    if (editing.isEditing(key)) {
        return (
            <SaveLocation
                location={location}
                onCancel={editing.stopEditing}
                onSuccess={editing.stopEditing}
            />
        );
    }

    const disabled = editing.isDisabled(key);

    return (
        <SettingsRow
            label={location.name}
            badge={location.isDefault && <DefaultMarker />}
            caption={<div>{formatAddressLine(location)}</div>}
            className={disabled ? 'opacity-50' : undefined}
            trailing={
                <div className="flex items-center gap-1">
                    <SettingsRowActionButton
                        aria-label={`Edit ${location.name}`}
                        disabled={disabled}
                        onClick={() => editing.startEditing(key)}
                    >
                        Edit
                    </SettingsRowActionButton>
                    {/* The default location can only be edited — delete and make-default never
                        render on it. */}
                    {!location.isDefault && (
                        <>
                            <RemoveLocationButton location={location} disabled={disabled} />
                            <SetDefaultLocationButton location={location} disabled={disabled} />
                        </>
                    )}
                </div>
            }
        />
    );
}

type Props = {
    editing: PracticeDetailsEditing;
};

export function LocationsSection({ editing }: Props) {
    const { data: locations } = useSuspenseQuery(practiceLocationsOptions());
    const isAdding = editing.isEditing(ADD_LOCATION_KEY);
    const isAddDisabled = editing.isDisabled(ADD_LOCATION_KEY);

    // A refetch can drop the location being edited (e.g. another member deleted it, and the
    // update's 404 triggered a refetch). Its row unmounts along with the only Cancel button that
    // could clear the lock, so clear it here whenever the editing key no longer names a location.
    useEffect(() => {
        const key = editing.currentKey;
        if (!key || key === ADD_LOCATION_KEY || !key.startsWith(LOCATION_KEY_PREFIX)) return;
        if (!locations.some((location) => locationKey(location.id) === key)) {
            editing.stopEditing();
        }
    }, [editing, locations]);

    return (
        <SettingsSection title="Locations" className="pb-10">
            {locations.map((location) => (
                <LocationRow key={location.id} location={location} editing={editing} />
            ))}
            {isAdding ? (
                <SaveLocation onCancel={editing.stopEditing} onSuccess={editing.stopEditing} />
            ) : (
                <Button
                    type="button"
                    variant="outline"
                    disabled={isAddDisabled}
                    onClick={() => editing.startEditing(ADD_LOCATION_KEY)}
                >
                    Add Location
                </Button>
            )}
        </SettingsSection>
    );
}
